import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Runs once after a fresh login (Google OAuth or email).
 * If the user's email matches an unclaimed studio → redirect to /claim.
 * Only fires on routes where it makes sense (/, /auth) and only once per session.
 */
export function useAutoClaimCheck() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const checkedRef = useRef(false);

  useEffect(() => {
    // Only check once per mount / session
    if (checkedRef.current) return;
    // Wait for auth to finish loading
    if (loading) return;
    // No user → nothing to check
    if (!user?.email) return;

    // Only auto-check on landing page (OAuth redirect target) or auth page
    const path = location.pathname;
    if (path !== "/" && path !== "/auth") return;

    // Don't interfere if user is already heading somewhere specific
    const params = new URLSearchParams(location.search);
    if (params.get("invite") || params.get("claim") || params.get("studio")) return;

    checkedRef.current = true;

    const checkMatch = async () => {
      try {
        // Also check if user already has an artist profile with onboarding done
        // If so, they're a returning user — skip the check
        const { data: profile } = await supabase
          .from("artist_profiles")
          .select("id, onboarding_completed")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profile?.onboarding_completed) return; // returning user, no claim needed

        const { data: matchedStudio } = await supabase
          .from("unclaimed_profiles")
          .select("google_place_id, name")
          .eq("email", user.email!)
          .is("claimed_by", null)
          .limit(1)
          .maybeSingle();

        if (matchedStudio?.google_place_id) {
          toast({
            title: "Studio gevonden! 🎉",
            description: `We hebben "${matchedStudio.name}" gevonden die gekoppeld is aan je e-mail. Claim nu je studio.`,
          });
          navigate("/claim?studio=" + matchedStudio.google_place_id);
        }
      } catch {
        // Silently fail — don't block normal flow
      }
    };

    checkMatch();
  }, [user, loading, location.pathname, location.search, navigate, toast]);
}
